import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROBLEMS } from "@/lib/siteData";
import { LANDING } from "@/constants/testIds";
import { Link } from "react-router-dom";

export const WaterProblems = () => {
  return (
    <section className="relative py-12 lg:py-16 bg-slate-50 overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-50" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Common Water Problems</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05] max-w-2xl">
              Notice something wrong? We can <span className="water-text">diagnose it.</span>
            </h2>
          </div>
          <p className="text-base text-slate-600 max-w-sm">
            Hover or tap any issue to see the recommended test.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {PROBLEMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                {/* Each problem card links straight to the booking page so
                    a visitor who recognises their issue can start a quote
                    in one tap. Slug carried in the query string so the
                    booking page can pre-select / track-source if it ever
                    wants to. */}
                <Link
                  to={`/book-water-test?problem=${encodeURIComponent(p.slug)}`}
                  data-testid={LANDING.problemCard(p.slug)}
                  aria-label={`Book a water test for ${p.title}`}
                  className="group relative block rounded-2xl bg-white p-5 md:p-6 border border-slate-100 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                >
                  <div className="absolute -right-8 -top-8 w-24 h-24 bg-cyan-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 group-hover:bg-cyan-500 group-hover:border-cyan-500 flex items-center justify-center transition-all duration-300">
                      <Icon className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-slate-900 text-base lg:text-lg leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-xs text-slate-500 leading-snug min-h-[2.5rem]">
                      {p.hint}
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cyan-600 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all">
                      Test for this <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/book-water-test"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Get answers about your water →
          </Link>
        </div>
      </div>
    </section>
  );
};
